import React, { useCallback, useEffect, useRef, useState } from "react";

import CameraPanel from "./CameraPanel";
import CaptureSettings from "./CaptureSettings";
import FilterBar from "./FilterBar";
import FramePicker from "./FramePicker";
import StickerPanel from "./StickerPanel";
import StripCanvas from "./StripCanvas";

import {
    DEFAULT_SETTINGS,
    STORAGE_KEY,
    normalizeSettings
} from "../constants/captureSettings";
import { DEFAULT_FILTER_ID, getFilter, supportsCanvasFilter } from "../constants/filters";
import { PHOTOS_PER_STRIP } from "../constants/frames";
import { DEFAULT_STICKER_SIZE } from "../constants/stickers";

import { useCaptureSequence } from "../hooks/useCaptureSequence";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useStickers } from "../hooks/useStickers";
import { useStrip } from "../hooks/useStrip";

import { beepLastTick, beepShutter, beepTick, primeAudio } from "../utils/beep";
import { keepOnlyFilter } from "../utils/filterCache";
import { loadStickerImage, nextStickerPosition } from "../utils/stickerDraw";

import { buttonStyle, primaryButton } from "../styles/ui";

export default function PhotoBooth() {
    const webcamRef = useRef(null);
    const stripRef = useRef(null);

    const isMobile = useMediaQuery("(max-width: 640px)");

    const [selectedFrame, setSelectedFrame] = useState(null);
    const [frameImg, setFrameImg] = useState(null);
    const [mode, setMode] = useState("photo");
    const [selectedFilter, setSelectedFilter] = useState(DEFAULT_FILTER_ID);

    const [flashing, setFlashing] = useState(false);

    const [settings, , setSetting] = useLocalStorage(
        STORAGE_KEY,
        DEFAULT_SETTINGS,
        normalizeSettings
    );

    const { photos, addPhoto, movePhoto, redoLastPhoto, resetStrip } = useStrip(selectedFrame);
    const {
        stickers,
        selectedStickerId,
        selectSticker,
        addSticker,
        updateSticker,
        removeSticker,
        clearStickers
    } = useStickers();

    const activeFilter = getFilter(selectedFilter);
    const photoCount = photos.length;
    // Số ô của khung đang chọn (3 hoặc 4 tuỳ khung). Khi chưa chọn khung dùng
    // giá trị mặc định để nhãn không nhảy số.
    const stripSize = selectedFrame ? selectedFrame.slots.length : PHOTOS_PER_STRIP;

    // Nạp ảnh khung
    useEffect(() => {
        if (!selectedFrame) {
            setFrameImg(null);
            return;
        }

        let cancelled = false;
        const img = new Image();
        img.src = selectedFrame.src;
        img.onload = () => {
            if (!cancelled) setFrameImg(img);
        };

        return () => {
            cancelled = true;
        };
    }, [selectedFrame]);

    useEffect(() => keepOnlyFilter(selectedFilter), [selectedFilter]);

    const acceptPhoto = img => {
        const stripFull = addPhoto(img);
        if (stripFull) setMode("decorate");
    };

    // Chụp một tấm. Trả về Promise để chuỗi chụp tự động biết lúc nào ảnh đã
    // thật sự được nạp xong rồi mới đếm ngược tấm kế tiếp.
    const takeShot = () =>
        new Promise(resolve => {
            const cam = webcamRef.current;
            if (!cam) {
                resolve(false);
                return;
            }

            const grab = () => {
                if (settings.sound) beepShutter();
                const src = cam.getScreenshot();
                setFlashing(false);

                if (!src) {
                    resolve(false);
                    return;
                }

                const img = new Image();
                img.onload = () => {
                    acceptPhoto(img);
                    resolve(true);
                };
                img.onerror = () => resolve(false);
                img.src = src;
            };

            // Loé sáng TRƯỚC khi chụp 120ms: để ánh sáng màn hình kịp hắt lên
            // mặt và webcam kịp bù sáng. Loé đúng lúc chụp thì gần như vô ích.
            if (settings.flash) {
                setFlashing(true);
                setTimeout(grab, 120);
            } else {
                grab();
            }
        });

    const handleTick = left => {
        if (!settings.sound) return;
        // Giây cuối kêu khác đi để biết mà chuẩn bị tư thế.
        if (left === 1) beepLastTick();
        else beepTick();
    };

    const { countdown, running, start, stop } = useCaptureSequence({
        seconds: settings.countdown,
        onShoot: takeShot,
        onTick: handleTick
    });

    const remaining = stripSize - photoCount;
    const canShoot = remaining > 0 && !running;

    // Ô sắp được lấp — khung cắt phải vẽ theo đúng ô này vì các khung có ô
    // không cùng tỉ lệ (khung 3 có một ô riêng, khung 4 cả bốn ô đều khác).
    const cropSlot =
        settings.grid && selectedFrame
            ? selectedFrame.slots[Math.min(photoCount, stripSize - 1)]
            : null;

    const captureLabel =
        settings.shotMode !== "auto"
            ? "Chụp ảnh"
            : remaining === stripSize
            ? `Chụp cả dải (${stripSize} tấm)`
            : `Chụp tiếp ${remaining} tấm`;

    const handleCapture = () => {
        if (!canShoot) return;

        // Phải đánh thức WebAudio NGAY trong sự kiện bấm chuột — trình duyệt
        // chặn âm thanh nếu AudioContext được tạo ngoài cử chỉ của người dùng.
        if (settings.sound) primeAudio();

        // Chế độ tự động: chụp nốt số tấm còn thiếu trong một lượt.
        start(settings.shotMode === "auto" ? remaining : 1);
    };

    const handleCancel = () => {
        stop();
        setFlashing(false);
    };

    const handleBack = () => {
        handleCancel();

        if (mode === "decorate") {
            setMode("photo");
            clearStickers();
            return;
        }

        setSelectedFrame(null);
        setMode("photo");
        setSelectedFilter(DEFAULT_FILTER_ID);
        resetStrip();
        clearStickers();
    };

    const uploadPhoto = e => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.src = reader.result;
            img.onload = () => acceptPhoto(img);
        };

        reader.readAsDataURL(file);
        e.target.value = "";
    };

    // Thêm hình dán vào giữa dải, rải dần để không cái nào chồng khít cái nào.
    const handleAddSticker = item => {
        if (!frameImg) return;

        const place = () =>
            addSticker({
                ...item,
                ...nextStickerPosition(frameImg, stickers.length),
                size: DEFAULT_STICKER_SIZE,
                rotation: 0,
                flipX: false
            });

        // Hình dán ảnh phải tải xong mới vẽ được; emoji thì vẽ được ngay.
        if (item.kind === "image") loadStickerImage(item.src).then(place).catch(() => {});
        else place();
    };

    const deleteSelectedSticker = useCallback(() => {
        if (selectedStickerId != null) removeSticker(selectedStickerId);
    }, [selectedStickerId, removeSticker]);

    useEffect(() => {
        const handleKeyDown = e => {
            // Bỏ qua khi con trỏ đang trong ô nhập chữ, nếu không Backspace lúc
            // gõ lời nhắn sẽ xoá mất hình dán.
            const t = e.target;
            if (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable) return;

            if ((e.key === "Delete" || e.key === "Backspace") && mode === "decorate") {
                deleteSelectedSticker();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [mode, deleteSelectedSticker]);

    // Bấm "Tải xuống": trên điện thoại mở thẳng hộp chia sẻ của hệ điều hành để
    // bấm "Lưu vào Ảnh" là ảnh vào luôn thư viện; trên desktop thì tải file như
    // thường. Không qua popup trung gian.
    const downloadPhoto = async () => {
        const strip = stripRef.current;
        if (!strip) return;

        const blob = await strip.toBlob();
        if (!blob) return;

        const fileName = "photo-strip.png";
        const file = new File([blob], fileName, { type: "image/png" });

        const isMobile =
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
            );

        // Chỉ dùng hộp chia sẻ trên điện thoại (iOS/Android): người dùng bấm
        // "Lưu vào Ảnh"/"Save to Photos" là ảnh vào thẳng thư viện. Trên desktop
        // thì bỏ qua để tải file trực tiếp cho nhanh.
        if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
                await navigator.share({ files: [file], title: "LoveGift Photobooth" });
                return;
            } catch (err) {
                // Người dùng bấm huỷ: dừng. Lỗi khác: rơi xuống tải file bên dưới.
                if (err && err.name === "AbortError") return;
            }
        }

        // Desktop / máy không hỗ trợ chia sẻ file: tải bằng object URL.
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 100);
    };

    return (
        <div style={centerCol}>
            <div style={topBar}>
                {selectedFrame && (
                    <button
                        style={isMobile ? backButtonCompact : backButton}
                        onClick={handleBack}
                        aria-label="Quay lại"
                    >
                        {isMobile ? "←" : "← Quay lại"}
                    </button>
                )}

                <h1 style={selectedFrame ? { ...titleBar, ...titleWithBack } : titleBar}>
                    {!selectedFrame
                        ? "₊✩‧₊˚ Chọn khung ảnh ౨ৎ ˚₊✩‧₊"
                        : mode === "photo"
                        ? "⋆｡‧˚ʚ Cười lên nào :)ɞ˚‧｡⋆"
                        : ". ݁₊ ⊹ . ݁Trang trí thôi . ⊹ ₊ ݁."}
                </h1>
            </div>

            <div style={mainContent}>
                {!selectedFrame ? (
                    <FramePicker onSelect={setSelectedFrame} />
                ) : (
                    <div style={row}>
                        <div style={{ width: "100%", maxWidth: isMobile ? 320 : 400 }}>
                            {mode === "photo" && (
                                <>
                                    <CameraPanel
                                        webcamRef={webcamRef}
                                        filterCss={activeFilter.css}
                                        mirrored={settings.mirror}
                                        countdown={countdown}
                                        flashing={flashing}
                                        running={running}
                                        cropSlot={cropSlot}
                                        photoCount={photoCount}
                                        stripSize={stripSize}
                                        canShoot={canShoot}
                                        captureLabel={captureLabel}
                                        onCapture={handleCapture}
                                        onCancel={handleCancel}
                                        onUpload={uploadPhoto}
                                        onRedo={redoLastPhoto}
                                    />
                                    {/* Thanh hiệu ứng đặt ngay dưới khung camera cho
                                        dễ chọn khi đang canh khung, trên bảng cài đặt. */}
                                    {supportsCanvasFilter && (
                                        <FilterBar
                                            value={selectedFilter}
                                            onChange={setSelectedFilter}
                                        />
                                    )}
                                    <CaptureSettings
                                        settings={settings}
                                        onChange={setSetting}
                                        disabled={running}
                                        stripSize={stripSize}
                                    />
                                </>
                            )}

                            {mode === "decorate" && (
                                <>
                                    <StickerPanel
                                        onAdd={handleAddSticker}
                                        onDelete={deleteSelectedSticker}
                                        hasSelection={selectedStickerId != null}
                                    />
                                    {supportsCanvasFilter && (
                                        <FilterBar
                                            value={selectedFilter}
                                            onChange={setSelectedFilter}
                                        />
                                    )}
                                </>
                            )}
                        </div>

                        <div>
                            <StripCanvas
                                ref={stripRef}
                                frame={selectedFrame}
                                frameImg={frameImg}
                                photos={photos}
                                stickers={stickers}
                                selectedStickerId={selectedStickerId}
                                filter={activeFilter}
                                mode={mode}
                                onMovePhoto={movePhoto}
                                onMoveSticker={updateSticker}
                                onSelectSticker={selectSticker}
                            />

                            {mode === "decorate" && (
                                <div style={downloadRow}>
                                    <button style={primaryButton} onClick={downloadPhoto}>
                                        ⬇ Tải xuống
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const centerCol = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 20,
    width: "100%",
    padding: "0 12px",
    boxSizing: "border-box"
};

const topBar = {
    position: "relative",
    width: "100%",
    maxWidth: 700,
    minHeight: 60,
    marginBottom: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
};

// Nút quay lại tách khỏi luồng để tiêu đề luôn nằm chính giữa và không bao giờ
// đè lên nhau khi tiêu đề dài phải xuống dòng.
const backButton = {
    ...buttonStyle,
    position: "absolute",
    left: 0,
    top: "50%",
    transform: "translateY(-50%)",
    height: 40,
    padding: "0 14px",
    fontSize: "clamp(14px, 3.5vw, 20px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
};

const backButtonCompact = {
    ...backButton,
    width: 44,
    padding: 0,
    fontSize: 22
};

// Chừa hai bên để tiêu đề vẫn cân giữa mà không chạm nút quay lại.
const titleWithBack = {
    padding: "0 52px"
};

const row = {
    display: "flex",
    gap: 40,
    alignItems: "flex-start",
    justifyContent: "center",
    flexWrap: "wrap",
    width: "100%"
};

const titleBar = {
    margin: 0,
    lineHeight: 1.2,
    textAlign: "center",
    flex: 1,
    minWidth: 0,
    fontSize: "clamp(18px, 5vw, 30px)",
    // Chữ kem vàng cổ điển để đọc rõ trên nền vintage tối.
    color: "#e9c9a4"
};

const mainContent = {
    // minHeight thay cho height cố định: bảng cài đặt mở ra sẽ cao hơn 600px,
    // để height cứng thì phần dưới bị tràn ra ngoài.
    minHeight: 600,
    width: "100%",
    maxWidth: 700,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start"
};

const downloadRow = {
    marginTop: 16,
    display: "flex",
    justifyContent: "center"
};
